<template>
    <q-card style="max-width: 100vw; height: 90vh; display: flex; flex-direction: column;">
        <q-toolbar class="bg-primary text-white">
            <q-toolbar-title>
                <div class="text-h6 capitalize">{{ title }}</div>
                <div class="text-caption text-white-7">{{ producto.productoDesc }}</div>
            </q-toolbar-title>
            <q-space />
            <q-btn dense flat icon="close" v-close-popup>
                <q-tooltip>Cerrar (ESC)</q-tooltip>
            </q-btn>
        </q-toolbar>

        <div class="row no-wrap content-scrollable">
            <!-- Columna principal: Formulario -->
            <div class="col-12 col-md-8 q-pa-md scroll" style="max-height: 75vh; overflow-y: auto;">
                <!-- Sección: Imagen del producto -->
                <q-card flat bordered class="q-mb-md">
                    <q-img
                        :src="imgSrc"
                        alt="product image"
                        fit="contain"
                        style="height: 200px"
                        :placeholder-src="noImagePath"
                        @click="imageUpdate"
                    />
                    <input
                        type="file"
                        ref="image"
                        style="display: none"
                        @change="onImageChange"
                    />
                </q-card>

                <!-- Sección: Información del Producto -->
                <q-expansion-item default-opened icon="info" label="Información del Producto"
                    header-class="bg-grey-2 text-primary text-weight-medium"
                    class="q-mb-md rounded-borders overflow-hidden">
                    <q-card flat bordered>
                        <q-card-section>
                            <ProductosCommon
                                v-model:empresa-id="form.empresa_id"
                                v-model:alicuota-id="form.alicuota_id"
                                v-model:producto-id="form.producto_id"
                                v-model:tipo-venta-id="form.tipo_venta_id"
                                v-model:ubicacion-id="form.ubicacion_id"
                                @update:empresa-id="validate($event, 'empresa_id')"
                                @update:alicuota-id="validate($event, 'alicuota_id')"
                                @update:producto-id="validate($event, 'producto_id')"
                                @update:tipo-venta-id="validate($event, 'tipo_venta_id')"
                                @update:ubicacion-id="validate($event, 'ubicacion_id')"
                                :res="res"
                            />
                        </q-card-section>
                    </q-card>
                </q-expansion-item>

                <!-- Sección: Precio y Descuentos -->
                <q-expansion-item default-opened icon="payments" label="Precio y Descuentos"
                    caption="Configure precio, cantidad y descuentos"
                    header-class="bg-grey-2 text-primary text-weight-medium"
                    class="q-mb-md rounded-borders overflow-hidden">
                    <q-card flat bordered>
                        <q-card-section>
                            <div class="row q-col-gutter-md">
                                <div class="col-12 col-md-6">
                                    <GltnInputNumber
                                        name="Precio Unitario"
                                        v-model.number="form.precio_unitario"
                                        hide-hint
                                        prefix="US$"
                                        label="Precio Unitario"
                                        @update:model-value="validate($event, 'precio_unitario')"
                                        :errors="res.getErrors('precio_unitario')"
                                    >
                                        <template v-slot:prepend>
                                            <q-icon name="attach_money" color="positive" />
                                        </template>
                                    </GltnInputNumber>
                                </div>
                                <div class="col-12 col-md-6">
                                    <GltnInputNumber
                                        v-model.number="form.cantidad"
                                        label="Cantidad"
                                        @update:model-value="validate($event, 'cantidad')"
                                        :errors="res.getErrors('cantidad')"
                                    >
                                        <template v-slot:prepend>
                                            <q-icon name="inventory_2" />
                                        </template>
                                    </GltnInputNumber>
                                </div>
                                <div class="col-12 col-md-6">
                                    <GltnInputNumber
                                        v-model.number="form.descuento_comercial"
                                        label="Descuento comercial"
                                        suffix="%"
                                        @update:model-value="validate($event, 'descuento_comercial')"
                                        :errors="res.getErrors('descuento_comercial')"
                                    >
                                        <template v-slot:prepend>
                                            <q-icon name="local_offer" />
                                        </template>
                                    </GltnInputNumber>
                                </div>
                                <div class="col-12 col-md-6" v-if="canShowEstrategico">
                                    <GltnInputNumber
                                        v-model.number="form.descuento_estrategico"
                                        label="Descuento estratégico"
                                        suffix="%"
                                        @update:model-value="validate($event, 'descuento_estrategico')"
                                        :errors="res.getErrors('descuento_estrategico')"
                                    >
                                        <template v-slot:prepend>
                                            <q-icon name="trending_down" />
                                        </template>
                                    </GltnInputNumber>
                                </div>
                            </div>
                        </q-card-section>
                    </q-card>
                </q-expansion-item>

                <!-- Sección: Observaciones -->
                <q-expansion-item icon="notes" label="Observaciones" caption="Agregue notas adicionales (opcional)"
                    header-class="bg-grey-2 text-primary text-weight-medium" class="rounded-borders overflow-hidden">
                    <q-card flat bordered>
                        <q-card-section>
                            <GltnInput
                                v-model="form.observaciones"
                                label="Observaciones"
                                type="textarea"
                                rows="3"
                                hint="Información adicional sobre este servicio"
                            >
                                <template v-slot:prepend>
                                    <q-icon name="comment" />
                                </template>
                            </GltnInput>
                        </q-card-section>
                    </q-card>
                </q-expansion-item>
            </div>

            <!-- Columna lateral: Resumen -->
            <ProductoResumen
                type="servicio"
                :form="form"
                :producto="producto"
                :validation-progress="validationProgress"
                :validation-progress-text="validationProgressText"
            >
                <template #after-card>
                    <ProductoResumenTips>
                        <li>Presiona <kbd>Ctrl + S</kbd> para guardar rápido</li>
                        <li>Los campos marcados con <q-icon name="error" size="14px" color="warning" /> son requeridos</li>
                        <li>El servicio se calcula por cantidad × precio unitario</li>
                    </ProductoResumenTips>
                </template>
            </ProductoResumen>
        </div>

        <q-separator />

        <!-- Footer con acciones -->
        <q-card-actions class="q-pa-md actions-fixed">
            <div class="row full-width q-gutter-sm">
                <div class="col">
                    <q-btn outline label="Cancelar" color="grey-7" icon="close" v-close-popup class="full-width" />
                </div>
                <div class="col" v-if="props.oper !== 'view'">
                    <q-btn unelevated :label="props.oper === 'add' ? 'Agregar al carrito' : 'Actualizar'"
                        :color="res.valid ? 'primary' : 'grey'"
                        :icon="props.oper === 'add' ? 'add_shopping_cart' : 'save'" @click="cartSave"
                        :disable="!res.valid" :loading="loading" class="full-width">
                        <template v-slot:loading>
                            <q-spinner-hourglass size="20px" />
                        </template>
                    </q-btn>
                </div>
            </div>
        </q-card-actions>

        <q-inner-loading :showing="loading">
            <q-spinner-hourglass size="50px" color="primary" />
        </q-inner-loading>
    </q-card>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from "vue";
import { round } from "mathjs";
import { useCartStore } from "@/stores/useCartStore.js";
import { useImageUploader } from "@/composables/useImageUploader";
import { usePage } from '@inertiajs/vue3'
import { usePrecioCalc } from "@/composables/usePrecioCalc";
import GltnInput from "@/components/form/GltnInput.vue";
import GltnInputNumber from "@/components/form/GltnInputNumber.vue";
import ProductoResumen from "../ProductoResumen.vue";
import ProductoResumenTips from "../ProductoResumenTips.vue";
import ProductosCommon from "./components/ProductosCommon.vue";
import suite from "./ProductoServicioValidation.js";
import { useProductoTitle } from "@/composables/useProductoTitle.js";

const page = usePage()
const productosImagesPath = page.props.productosImagesPath
const noImagePath = page.props.noImagePath
const imgSrc = computed(
    () => `${productosImagesPath}/${props.producto.productoID}.webp?${random.value}` // Use random to force reload image`
);
const canShowEstrategico = computed(() => page.props?.auth?.can.includes('presupuesto.item.estrategico'));

const props = defineProps({
    oper: String,
    producto: Object,
    formulario: Object,
    presupId: Number,
    isSavedPresup: {
        type: Boolean,
        default: false,
    },
});
    const emits = defineEmits(["added", "updated"]);

const form = reactive({
    alicuota_id: 3,
    cantidad: "",
    descuento_comercial: 0,
    descuento_estrategico: 0,
    descuento_monto: 0,
    empresa_id: "",
    precio_unitario: round(props.producto.precio, 2),
    subtotal: "",
    tipo_venta_id: "",
    total: "",
    ubicacion_id: "",
});
// TITULO
const title = useProductoTitle(props.oper, 'servicio');
// VALIDACION
const res = ref();
res.value = suite.get();
const validate = (value, name) => {
    res.value = suite(name, form);
};
const setValues = () => {
    for (let key in props.formulario) {
        form[key] = props.formulario[key];
    }
};
onMounted(() => {
    if (props.oper === "edit" || props.oper === 'view') {
        setValues();
    }
    validate();
});

const {
    total,
    subtotal,
    precio_unitario,
    descuento_comercial,
    descuento_estrategico,
} = usePrecioCalc(form);

// VALIDACION PROGRESS
const validationProgress = computed(() => {
    const requiredFields = ['empresa_id', 'alicuota_id', 'tipo_venta_id', 'cantidad', 'precio_unitario', 'ubicacion_id'];
    const completed = requiredFields.filter(field => {
        const value = form[field];
        return value !== '' && value !== null && value !== undefined;
    }).length;
    return completed / requiredFields.length;
});

const validationProgressText = computed(() => {
    const percent = Math.round(validationProgress.value * 100);
    return `Formulario ${percent}% completo`;
});

// GESTION DE CART
const cartStore = useCartStore();
const loading = computed(() => cartStore.getLoadingStatus);
const cartSave = () => {
    if (props.oper === "add") {
        cartAdd();
    } else {
        cartUpdate();
    }
};
const cartAdd = async () => {
    await cartStore.itemAdd(
        {
            form: { ...form },
            producto: { ...props.producto },
        },
        props.isSavedPresup,
        props.presupId
    );
    emits("added");
};
const cartUpdate = async () => {
    await cartStore.itemUpdate(
        {
            form: { ...form },
            producto: { ...props.producto },
        },
        props.isSavedPresup,
        props.presupId
    );
    emits("updated");
};
const { image, onImageChange, imageUpdate, random } = useImageUploader(form,props);
</script>
